// The full-screen map surface: clustered emoji markers over a Leaflet map, the
// All/My List toggle, the independent map filter sheet, and the tap-to-open
// venue sheet. Extracted from App.js; App.js is the only consumer.
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { SlidersHorizontal, Search, X } from "lucide-react";
import {
  MELBOURNE_CENTER,
  MELBOURNE_ZOOM,
  OCCASION_OPTIONS,
  AMENITY_FILTERS,
  getVenueEmoji,
  venueMatchesAreas,
  buildAreaExtents,
  venueMatchesOccasions,
  venueMatchesPrice,
  venueMatchesAmenities,
  isVenueOpenNow,
  getTodayDayKey,
  venueOpenAt,
  timeStringToMinutes,
  OPEN_AT_DAY_OPTIONS,
  OPEN_AT_TIME_OPTIONS,
} from "../lib/venueLogic";
import {
  MapFilterGroup,
  MapFilterChip,
  MapFilterSection,
  SearchableChips,
  MapAreaFilter,
} from "./MapFilters";
import { MapVenueSheet } from "./MapVenueSheet";
import { VenueNightsSheet } from "./VenueNightsSheet";
import { VenueHistory } from "./VenueHistory";
import { searchPlaces, addGooglePlace } from "../lib/venueSearch";
import { ChevronLeft, MapPin, Plus, Minus, List, Map as MapIcon, Star } from "lucide-react";
import { spotCategory } from "./SpotSheet";

// Spot pins (Sep 6 — the friend knowledge layer): category emoji in a white
// circle with the olive ring. Deliberately unlike venue emoji pins — a spot
// is a tip, not a place to browse.
function createSpotIcon(category) {
  const c = spotCategory(category);
  return L.divIcon({
    html: `<div style="width:34px;height:34px;border-radius:50%;background:#fff;border:2.5px solid #455d3b;box-shadow:0 1px 3px rgba(0,0,0,0.25);display:flex;align-items:center;justify-content:center;font-size:16px;">${c.emoji}</div>`,
    className: "friend-checkin-icon",
    iconSize: [38, 38],
    iconAnchor: [19, 19],
  });
}
import { AddVenueSheet } from "./AddVenueSheet";
import { supabase } from "../supabaseClient";
import { timeAgoShort, FRESH_MS } from "../lib/checkins";

function createEmojiIcon(emoji) {
  return L.divIcon({
    html: `<div style="font-size:24px;line-height:1;text-align:center;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.25));">${emoji}</div>`,
    className: "venue-emoji-icon",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
  });
}

// --- Friends mode helpers ------------------------------------------------

const esc = (s) =>
  String(s || "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );

// One pin per venue: up to 3 stacked friend avatars + a "Name · 2h" label.
// Fresh (<3h) check-ins get the olive ring; older-today pins fade.
function createFriendsIcon(group) {
  const fresh = group.entries.some((e) => {
    // Age clamped to past-only (Oct 16): a FUTURE plan's negative age sailed
    // under the window and lit the live ring for a night not yet happening.
    const age = Date.now() - new Date(e.created_at).getTime();
    return age >= 0 && age < FRESH_MS;
  });
  const ring = fresh ? "#455d3b" : "#b5b2ab";
  const shown = group.entries.slice(0, 3);
  const avatars = shown
    .map((e, i) => {
      const style = `width:30px;height:30px;border-radius:50%;border:2px solid ${ring};box-shadow:0 1px 3px rgba(0,0,0,0.3);${i > 0 ? "margin-left:-9px;" : ""}`;
      if (e.profile?.avatar_url) {
        return `<img src="${esc(e.profile.avatar_url)}" style="${style}object-fit:cover;background:#fff;" />`;
      }
      const initial = esc(
        (e.profile?.display_name || "?").trim().charAt(0).toUpperCase()
      );
      return `<div style="${style}background:#455d3b;color:#fff;display:flex;align-items:center;justify-content:center;font:600 13px sans-serif;">${initial}</div>`;
    })
    .join("");
  const first = (group.entries[0].profile?.display_name || "A friend").split(" ")[0];
  const extra = group.entries.length > 1 ? ` +${group.entries.length - 1}` : "";
  // "What's on" label rides along on the pin, truncated so pins stay pins.
  const whatsOn = group.entries[0].label
    ? ` · ${group.entries[0].label.length > 16 ? group.entries[0].label.slice(0, 15) + "…" : group.entries[0].label}`
    : "";
  const label = `${esc(first)}${extra}${esc(whatsOn)} · ${timeAgoShort(group.entries[0].created_at)}`;
  return L.divIcon({
    html: `<div style="display:flex;flex-direction:column;align-items:center;${fresh ? "" : "opacity:0.6;"}">
      <div style="display:flex;">${avatars}</div>
      <div style="margin-top:2px;background:#fff;border-radius:9999px;padding:1px 7px;font:600 10px sans-serif;color:${fresh ? "#455d3b" : "#6b6a65"};box-shadow:0 1px 2px rgba(0,0,0,0.25);white-space:nowrap;">${label}</div>
    </div>`,
    className: "friend-checkin-icon",
    iconSize: [90, 52],
    iconAnchor: [45, 48],
  });
}

// --- Past lens icons (Sep 6 — the memory map) ----------------------------
// Three pin weights, by how much lives at the venue: one person's one night
// = their avatar; multiple nights/people = an olive cluster chip (repeat
// venues visibly accumulate weight); bare been-marks only = a quiet dot.

function pastAvatarHtml(profile, size, extra = "") {
  const style = `width:${size}px;height:${size}px;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 2px rgba(0,0,0,0.25);${extra}`;
  if (profile?.avatar_url) {
    return `<img src="${esc(profile.avatar_url)}" style="${style}object-fit:cover;background:#fff;" />`;
  }
  const initial = esc(
    (profile?.display_name || "?").trim().charAt(0).toUpperCase()
  );
  return `<div style="${style}background:#455d3b;color:#fff;display:flex;align-items:center;justify-content:center;font:600 ${Math.round(size * 0.42)}px sans-serif;">${initial}</div>`;
}

function createPastSingleIcon(profile) {
  return L.divIcon({
    html: `<div style="display:flex;">${pastAvatarHtml(profile, 30, "border-color:#455d3b;")}</div>`,
    className: "friend-checkin-icon",
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });
}

function createPastClusterIcon(group) {
  const people = [];
  const seen = new Set();
  for (const n of group.nights) {
    for (const e of n.entries) {
      if (!seen.has(e.user_id)) {
        seen.add(e.user_id);
        people.push(e.profile);
      }
      if (people.length >= 2) break;
    }
    if (people.length >= 2) break;
  }
  const avatars = people
    .map((p, i) => pastAvatarHtml(p, 20, i > 0 ? "margin-left:-8px;" : ""))
    .join("");
  return L.divIcon({
    html: `<div style="display:inline-flex;align-items:center;gap:5px;background:#455d3b;border-radius:9999px;padding:3px 9px 3px 3px;box-shadow:0 1px 3px rgba(0,0,0,0.3);">
      <div style="display:flex;">${avatars}</div>
      <span style="font:600 11px sans-serif;color:#fff;">${group.nights.length}</span>
    </div>`,
    className: "friend-checkin-icon",
    iconSize: [70, 28],
    iconAnchor: [35, 14],
  });
}

function createPastMarkIcon() {
  return L.divIcon({
    html: `<div style="width:18px;height:18px;border-radius:50%;background:#455d3b;border:2px solid #fff;box-shadow:0 1px 2px rgba(0,0,0,0.25);display:flex;align-items:center;justify-content:center;"><div style="width:6px;height:6px;border-radius:50%;background:#fff;"></div></div>`,
    className: "friend-checkin-icon",
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

// One person's whole trail (July 25 — profile "Places" counter): a pin at
// every venue they've checked in, visit count + recency on the label.
function createPersonIcon(group, profile) {
  const style =
    "width:30px;height:30px;border-radius:50%;border:2px solid #455d3b;box-shadow:0 1px 3px rgba(0,0,0,0.3);";
  const avatar = profile?.avatar_url
    ? `<img src="${esc(profile.avatar_url)}" style="${style}object-fit:cover;background:#fff;" />`
    : `<div style="${style}background:#455d3b;color:#fff;display:flex;align-items:center;justify-content:center;font:600 13px sans-serif;">${esc(
        (profile?.display_name || "?").trim().charAt(0).toUpperCase()
      )}</div>`;
  const n = group.visits.length;
  const label = `${n > 1 ? `×${n} · ` : ""}${timeAgoShort(
    group.visits[0].created_at
  )}`;
  return L.divIcon({
    html: `<div style="display:flex;flex-direction:column;align-items:center;">
      <div>${avatar}</div>
      <div style="margin-top:2px;background:#fff;border-radius:9999px;padding:1px 7px;font:600 10px sans-serif;color:#455d3b;box-shadow:0 1px 2px rgba(0,0,0,0.25);white-space:nowrap;">${label}</div>
    </div>`,
    className: "friend-checkin-icon",
    iconSize: [90, 52],
    iconAnchor: [45, 48],
  });
}

function MapResizer() {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
      map.setView(MELBOURNE_CENTER, MELBOURNE_ZOOM);
    }, 100);
    return () => clearTimeout(timer);
  }, [map]);
  return null;
}

// Lifts the map's current viewport bounds into React state so the place count
// and the card's venue-to-venue swipe only cover what's actually on screen
// (zoom acts as an implicit filter — a CBD zoom shouldn't swipe to Preston).
function BoundsWatcher({ onBounds }) {
  const map = useMapEvents({
    moveend: () => onBounds(map.getBounds()),
    zoomend: () => onBounds(map.getBounds()),
  });
  useEffect(() => {
    onBounds(map.getBounds());
  }, [map, onBounds]);
  return null;
}

// Hands the Leaflet map instance up to MapScreen so search results can
// fly-to a pin (and, later, card swipes can keep the active pin in view).
function MapRef({ mapRef }) {
  const map = useMap();
  useEffect(() => {
    mapRef.current = map;
    return () => {
      if (mapRef.current === map) mapRef.current = null;
    };
  }, [map, mapRef]);
  return null;
}

export function MapScreen({ venues, savedIds, onSave, onUnsave, onHide, onCheckIn, onOpenThread, onOpenProfile, hiddenIds, areas = [], onVenueAdded, showToast, searchOpen, onSearchOpenChange, userId, personFilter = null, onPersonFilter, onClearPersonFilter, listFilter = null, onClearListFilter, onSetListFilter, onFiltersSnapshot, onOpenSpot, spotsRefresh = 0 }) {
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [mapFilter, setMapFilter] = useState("all");
  const [mapBounds, setMapBounds] = useState(null); // current Leaflet viewport
  // Search sheet visibility lives in App (controlled) so the FAB's "Check in"
  // shortcut can open it too; falls back to local state when uncontrolled.
  const [localSearch, setLocalSearch] = useState(false);
  const showSearch = searchOpen ?? localSearch;
  const setShowSearch = onSearchOpenChange ?? setLocalSearch;
  const mapRef = useRef(null);

  // Search-sheet tap on a pool venue: fly the map to the pin and open its
  // card. Also used after a Google add so the new venue is immediately shown.
  // openCard=false for the check-in-only flow — the CheckinSheet is the
  // destination there; a venue card lurking behind it is clutter.
  function flyToVenue(venue, { openCard = true } = {}) {
    // Fly-to MEANS the map (Oct 16): picking or adding a place from search
    // while the list view was open flew the map invisibly underneath it.
    setListView(false);
    const lat = Number(venue.latitude);
    const lng = Number(venue.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng) && mapRef.current) {
      mapRef.current.flyTo(
        [lat, lng],
        Math.max(mapRef.current.getZoom(), 16),
        { duration: 0.8 }
      );
    }
    if (openCard) setSelectedVenue(venue);
  }
  const [showFilters, setShowFilters] = useState(false);
  // Map list view accordion (Oct 15) — one venue's history open at a time.
  const [expandedListVenue, setExpandedListVenue] = useState(null);
  // CUSTOM LISTS as a filter dimension (Oct 12, Mark: "on the map when
  // selecting the filter we should see a dropdown Custom list") — picking
  // one applies the same lens as View-on-map; composable with every other
  // chip ("Sunday pubs, open now").
  const [myLists, setMyLists] = useState(null);
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const [{ data: ls }, { data: mem }] = await Promise.all([
        supabase
          .from("venue_lists")
          .select("id, name, emoji, owner_id")
          .order("created_at", { ascending: true }),
        supabase
          .from("venue_list_members")
          .select("list_id")
          .eq("user_id", userId),
      ]);
      if (cancelled) return;
      const memberIds = new Set((mem || []).map((m) => m.list_id));
      setMyLists(
        (ls || []).filter(
          (l) => l.owner_id === userId || memberIds.has(l.id)
        )
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);
  async function pickListFilter(l) {
    if (listFilter?.id === l.id) {
      onClearListFilter?.();
      setShowFilters(false);
      return;
    }
    const { data: items } = await supabase
      .from("venue_list_items")
      .select("venue_id")
      .eq("list_id", l.id);
    onSetListFilter?.({
      id: l.id,
      name: l.name,
      emoji: l.emoji || "📍",
      ids: new Set((items || []).map((i) => i.venue_id)),
    });
    setShowFilters(false);
  }
  // Map-only filter state. Deliberately LOCAL to MapScreen and independent of
  // the App-level swipe/match filters — toggling these never touches the match
  // setup, and vice versa.
  const [fOccasions, setFOccasions] = useState([]); // "What are you after?" chips
  const [fCuisines, setFCuisines] = useState([]);
  const [fAreas, setFAreas] = useState([]); // [{ name, lat, lng }]
  const [fOpenNow, setFOpenNow] = useState(false);
  // Open at a chosen day + time (Oct 30) — active only when BOTH are set.
  const [fOpenDay, setFOpenDay] = useState("");
  const [fOpenTime, setFOpenTime] = useState("");
  const [fMinRating, setFMinRating] = useState(0);
  const [fPrices, setFPrices] = useState([]); // price_level numbers 1..4
  const [fAmenities, setFAmenities] = useState([]); // amenity column keys

  // Suburb-first, same as sessions (July 25) — was a blunt 3km circle,
  // which is why the map and a session showed different places.
  const mapAreaExtents = useMemo(
    () => buildAreaExtents(venues, fAreas),
    [venues, fAreas]
  );

  // Uses the cleaned cuisine_bucket (backfilled from the taxonomy), not the raw
  // Google 'cuisine'. Venues with no real cuisine (formats/junk) have a null
  // bucket and simply don't appear under any cuisine chip.
  const cuisineOptions = useMemo(
    () => Array.from(new Set(venues.map((v) => v.cuisine_bucket).filter(Boolean))).sort(),
    [venues]
  );

  const activeCount =
    fOccasions.length +
    fCuisines.length +
    fAreas.length +
    fPrices.length +
    fAmenities.length +
    (fOpenNow ? 1 : 0) +
    (fMinRating > 0 ? 1 : 0);

  const plottable = useMemo(
    () =>
      venues.filter(
        (v) =>
          !(hiddenIds && hiddenIds.has(v.id)) &&
          Number.isFinite(Number(v.latitude)) &&
          Number.isFinite(Number(v.longitude))
      ),
    [venues, hiddenIds]
  );

  // --- Friends mode data ---------------------------------------------------
  // Lazy: fetched when the Friends segment is selected. Latest check-in per
  // friend from the last 24h; venue objects come from the already-loaded pool
  // (a friend's RLS-hidden manual venue simply doesn't pin).
  const [friendCheckins, setFriendCheckins] = useState(null); // null = loading
  useEffect(() => {
    if (mapFilter !== "friends" || !userId) return;
    let cancelled = false;
    setFriendCheckins(null);
    (async () => {
      const { data: fr } = await supabase
        .from("friendships")
        .select("requester_id, addressee_id")
        .eq("status", "accepted")
        .or(`requester_id.eq.${userId},addressee_id.eq.${userId}`);
      const friendIds = Array.from(
        new Set(
          (fr || []).map((f) =>
            f.requester_id === userId ? f.addressee_id : f.requester_id
          )
        )
      );
      if (friendIds.length === 0) {
        if (!cancelled) setFriendCheckins([]);
        return;
      }
      const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data: rows } = await supabase
        .from("activities")
        .select("user_id, venue_id, created_at, label")
        .eq("kind", "checkin")
        .in("user_id", friendIds)
        // Presence is the TOGGLE, not the timestamp (Aug, Mark's unified
        // form) — quiet check-ins never pin anyone to the live map.
        .eq("show_live", true)
        .gte("created_at", dayAgo)
        // Upcoming nights don't pin friends to the map before they happen
        // (Aug 1) — a future timestamp reads as fresh and drew a live ring.
        .lte("created_at", new Date().toISOString())
        .order("created_at", { ascending: false });
      // Latest check-in per friend = where they ARE (not their whole trail).
      const latestByUser = new Map();
      for (const r of rows || []) {
        if (!latestByUser.has(r.user_id)) latestByUser.set(r.user_id, r);
      }
      const latest = Array.from(latestByUser.values());
      let profById = {};
      let venById = {};
      if (latest.length > 0) {
        const [profsRes, vensRes] = await Promise.all([
          supabase
            .from("profiles")
            .select("id, display_name, username, avatar_url")
            .in("id", latest.map((r) => r.user_id)),
          // Direct fetch (not the curated pool) — open venue reads mean a
          // friend's check-in at their own manual venue still pins for you.
          supabase
            .from("venues")
            .select("*")
            .in("id", Array.from(new Set(latest.map((r) => r.venue_id)))),
        ]);
        profById = Object.fromEntries(
          (profsRes.data || []).map((p) => [p.id, p])
        );
        venById = Object.fromEntries(
          (vensRes.data || []).map((v) => [v.id, v])
        );
      }
      if (cancelled) return;
      setFriendCheckins(
        latest.map((r) => ({
          ...r,
          profile: profById[r.user_id] || null,
          venue: venById[r.venue_id] || null,
        }))
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [mapFilter, userId]);

  // --- Past lens data (Sep 6 — the memory map) ---------------------------
  // Friends segment splits into two time lenses: Now = live presence (the
  // 24h/show_live fetch above), Past = every venue with a night among you +
  // your friends, plus bare been-marks (friend-readable since Aug 20, still
  // SILENT — passive visibility only). Zero new SQL: activities read rides
  // can_see_activity, marks ride been_marks_friends_read. No show_live
  // filter here: presence is a choice, memory is not.
  // --- Inline search (Sep 6, new map UI — Mark: no Recent list, typing
  // searches places live). The circle expands to a field; results panel
  // sections lead with YOUR list/been, then friends' venues, then the
  // curated map, then Google places not on the map yet (dashed Add).
  const [searchUi, setSearchUi] = useState(false);
  const [q, setQ] = useState("");
  const [searchRes, setSearchRes] = useState({ venues: [], google: [] });
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const searchSeq = useRef(0);
  useEffect(() => {
    if (!searchUi) return;
    const term = q.trim();
    if (term.length < 2) {
      setSearchRes({ venues: [], google: [] });
      setSearching(false);
      return;
    }
    setSearching(true);
    const mySeq = ++searchSeq.current;
    const timer = setTimeout(async () => {
      try {
        const res = await searchPlaces(term, userId);
        if (mySeq === searchSeq.current) setSearchRes(res);
      } finally {
        if (mySeq === searchSeq.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [q, searchUi, userId]);

  function closeSearch() {
    setSearchUi(false);
    setQ("");
    setSearchRes({ venues: [], google: [] });
  }

  // FRIENDS DIRECTORY (Sep 6, Mark: "let me use the search to search my
  // friends and see their maps") — on the Friends view, opening search lists
  // your friends before you type; a tap applies the existing person filter
  // (their whole trail, the profile-Places map). Typing narrows by name.
  const [friendsDir, setFriendsDir] = useState(null); // null = not loaded
  useEffect(() => {
    if (!searchUi || mapFilter !== "friends" || !userId || friendsDir) return;
    let cancelled = false;
    (async () => {
      const { data: fr } = await supabase
        .from("friendships")
        .select("requester_id, addressee_id")
        .eq("status", "accepted")
        .or(`requester_id.eq.${userId},addressee_id.eq.${userId}`);
      const ids = Array.from(
        new Set(
          (fr || []).map((f) =>
            f.requester_id === userId ? f.addressee_id : f.requester_id
          )
        )
      );
      if (ids.length === 0) {
        if (!cancelled) setFriendsDir([]);
        return;
      }
      const { data: profs } = await supabase
        .from("profiles")
        .select("id, display_name, username, avatar_url")
        .in("id", ids);
      if (!cancelled)
        setFriendsDir(
          (profs || []).sort((a, b) =>
            (a.display_name || "").localeCompare(b.display_name || "")
          )
        );
    })();
    return () => {
      cancelled = true;
    };
  }, [searchUi, mapFilter, userId, friendsDir]);

  const friendMatches = useMemo(() => {
    if (mapFilter !== "friends" || !friendsDir) return [];
    const term = q.trim().toLowerCase();
    if (!term) return friendsDir;
    return friendsDir.filter(
      (p) =>
        (p.display_name || "").toLowerCase().includes(term) ||
        (p.username || "").toLowerCase().includes(term)
    );
  }, [mapFilter, friendsDir, q]);

  // Whose map is this? (Mark, Sep 6): Friends/Past pins are FRIENDS ONLY —
  // your own footprint lives under My List's Been lens, as your icon. Either
  // way the tap shows the venue's FULL picture, you and friends together.
  const [friendLens, setFriendLens] = useState("now"); // "now" | "past"
  const [myListLens, setMyListLens] = useState("saved"); // "saved" | "been"
  const [pastGroups, setPastGroups] = useState(null); // null = loading
  const wantPast =
    (mapFilter === "friends" && friendLens === "past") ||
    (mapFilter === "my_list" &&
      (myListLens === "been" || myListLens === "not_been")) ||
    searchUi; // search sections need the been/friends sets too
  useEffect(() => {
    if (!wantPast || !userId) return;
    let cancelled = false;
    setPastGroups(null);
    (async () => {
      const { data: fr } = await supabase
        .from("friendships")
        .select("requester_id, addressee_id")
        .eq("status", "accepted")
        .or(`requester_id.eq.${userId},addressee_id.eq.${userId}`);
      const everyone = Array.from(
        new Set([
          userId,
          ...(fr || []).map((f) =>
            f.requester_id === userId ? f.addressee_id : f.requester_id
          ),
        ])
      );
      const [actsRes, marksRes] = await Promise.all([
        supabase
          .from("activities")
          .select("id, user_id, venue_id, created_at, label, joined_from, is_album")
          .eq("kind", "checkin")
          .in("user_id", everyone)
          .not("venue_id", "is", null)
          // Upcoming nights are plans, not memories.
          .lte("created_at", new Date().toISOString())
          // Oct 9 doctrine: "Show on map" off pulls the memory pin.
          .eq("show_map", true)
          .order("created_at", { ascending: false })
          .limit(500),
        supabase
          .from("been_marks")
          .select("user_id, venue_id, created_at")
          .in("user_id", everyone)
          .limit(500),
      ]);
      const acts = actsRes.data || [];
      const marks = marksRes.data || [];
      const venueIds = Array.from(
        new Set([...acts.map((a) => a.venue_id), ...marks.map((m) => m.venue_id)])
      );
      if (venueIds.length === 0) {
        if (!cancelled) setPastGroups([]);
        return;
      }
      const profIds = Array.from(
        new Set([...acts.map((a) => a.user_id), ...marks.map((m) => m.user_id)])
      );
      const [profsRes, vensRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, display_name, username, avatar_url")
          .in("id", profIds),
        supabase.from("venues").select("*").in("id", venueIds),
      ]);
      if (cancelled) return;
      const profById = Object.fromEntries(
        (profsRes.data || []).map((p) => [p.id, p])
      );
      const venById = Object.fromEntries(
        (vensRes.data || []).map((v) => [v.id, v])
      );
      // Group per venue, then merge shared nights inside each venue via
      // joined_from edges (union toward the topmost ancestor present) — the
      // same edge the card merge uses, scoped to one venue so it stays cheap.
      const byVenue = new Map();
      const ensure = (venueId) => {
        const venue = venById[venueId];
        if (
          !venue ||
          !Number.isFinite(Number(venue.latitude)) ||
          !Number.isFinite(Number(venue.longitude))
        )
          return null;
        if (!byVenue.has(venueId))
          byVenue.set(venueId, { venue, rows: [], marks: [] });
        return byVenue.get(venueId);
      };
      for (const a of acts) {
        const g = ensure(a.venue_id);
        if (g) g.rows.push({ ...a, profile: profById[a.user_id] || null });
      }
      for (const m of marks) {
        const g = ensure(m.venue_id);
        // A mark is redundant next to that person's own check-in there.
        if (g && !g.rows.some((r) => r.user_id === m.user_id))
          g.marks.push({ ...m, profile: profById[m.user_id] || null });
      }
      const groups = [];
      for (const g of byVenue.values()) {
        const byId = new Map(g.rows.map((r) => [r.id, r]));
        const rootOf = (row) => {
          let cur = row;
          const seen = new Set();
          while (
            cur.joined_from &&
            byId.has(cur.joined_from) &&
            !seen.has(cur.id)
          ) {
            seen.add(cur.id);
            cur = byId.get(cur.joined_from);
          }
          return cur.id;
        };
        const nightMap = new Map();
        for (const r of g.rows) {
          const root = rootOf(r);
          if (!nightMap.has(root)) nightMap.set(root, []);
          nightMap.get(root).push(r); // rows arrive newest-first
        }
        const nights = Array.from(nightMap.values()).map((entries) => ({
          key: entries[0].id,
          entries,
        }));
        nights.sort(
          (a, b) =>
            new Date(b.entries[0].created_at) - new Date(a.entries[0].created_at)
        );
        groups.push({ venue: g.venue, nights, marks: g.marks });
      }
      setPastGroups(groups);
    })();
    return () => {
      cancelled = true;
    };
  }, [wantPast, userId]);

  // The map filters apply to the memory layers too (Mark, Sep 6: "all the
  // coffee places my friends have been") — same venue predicates as browse,
  // minus the verified/saved gating (a night there IS the qualification).
  const matchesMapFilters = useCallback((v) => {
    const todayKey = getTodayDayKey();
    if (fAreas.length > 0 && !venueMatchesAreas(v, fAreas, 0, mapAreaExtents))
      return false;
    if (fCuisines.length > 0 && !fCuisines.includes(v.cuisine_bucket))
      return false;
    if (fOccasions.length > 0 && !venueMatchesOccasions(v, fOccasions, todayKey))
      return false;
    if (fOpenNow && !isVenueOpenNow(v)) return false;
    if (
      fOpenDay &&
      fOpenTime &&
      !venueOpenAt(v, fOpenDay, timeStringToMinutes(fOpenTime))
    )
      return false;
    if (fMinRating > 0 && !(Number(v.rating) >= fMinRating)) return false;
    if (fPrices.length > 0 && !venueMatchesPrice(v, fPrices)) return false;
    if (fAmenities.length > 0 && !venueMatchesAmenities(v, fAmenities))
      return false;
    return true;
  }, [fAreas, fCuisines, fOccasions, fOpenNow, fOpenDay, fOpenTime, fMinRating, fPrices, fAmenities, mapAreaExtents]);

  // The two views over the same data. Friends/Past: only venues where a
  // FRIEND has a night or mark, pin faces friends-only. My List/Been: only
  // venues where YOU have a night or mark, pin is your icon.
  const friendPastPins = useMemo(() => {
    if (!pastGroups) return [];
    return pastGroups
      .filter((g) => matchesMapFilters(g.venue))
      .map((g) => ({
        ...g,
        friendNights: g.nights.filter((n) =>
          n.entries.some((e) => e.user_id !== userId)
        ),
        friendMarks: g.marks.filter((m) => m.user_id !== userId),
      }))
      .filter((g) => g.friendNights.length > 0 || g.friendMarks.length > 0);
  }, [pastGroups, userId, matchesMapFilters]);

  const myBeenPins = useMemo(() => {
    if (!pastGroups) return [];
    return pastGroups
      .filter((g) => matchesMapFilters(g.venue))
      .map((g) => {
        const ownNights = g.nights.filter((n) =>
          n.entries.some((e) => e.user_id === userId)
        );
        const ownMark = g.marks.find((m) => m.user_id === userId) || null;
        const ownProfile =
          ownNights[0]?.entries.find((e) => e.user_id === userId)?.profile ||
          ownMark?.profile ||
          null;
        return { ...g, ownNights, ownMark, ownProfile };
      })
      .filter((g) => g.ownNights.length > 0 || g.ownMark);
  }, [pastGroups, userId, matchesMapFilters]);

  // --- SPOTS (Sep 6 — toilets, study spots, parking; never public) -------
  // Plain select: RLS returns yours + accepted friends' rows, nothing else.
  // Whose-map split (Mark's field call, same as Past): Friends/Spots pins
  // FRIENDS' spots only; your own pin under My List's Spots lens.
  const [spots, setSpots] = useState(null); // null = loading
  const wantSpots =
    (mapFilter === "friends" && friendLens === "spots") ||
    (mapFilter === "my_list" && myListLens === "spots");
  useEffect(() => {
    if (!wantSpots || !userId) return;
    let cancelled = false;
    setSpots(null);
    (async () => {
      const [{ data: rows }, savesRes] = await Promise.all([
        supabase
          .from("spots")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(300),
        supabase.from("spot_saves").select("spot_id").eq("user_id", userId),
      ]);
      const savedSet = new Set(
        (savesRes.data || []).map((r) => r.spot_id)
      );
      const pIds = Array.from(new Set((rows || []).map((s) => s.user_id)));
      let pById = {};
      if (pIds.length > 0) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("id, display_name, username, avatar_url")
          .in("id", pIds);
        pById = Object.fromEntries((profs || []).map((p) => [p.id, p]));
      }
      if (cancelled) return;
      setSpots(
        (rows || [])
          .filter(
            (s) =>
              Number.isFinite(Number(s.lat)) && Number.isFinite(Number(s.lng))
          )
          .map((s) => ({
            ...s,
            profile: pById[s.user_id] || null,
            savedByMe: savedSet.has(s.id),
          }))
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [wantSpots, userId, spotsRefresh]);

  const friendSpots = useMemo(
    () => (spots || []).filter((s) => s.user_id !== userId),
    [spots, userId]
  );
  // Your Spots lens = spots you ADDED plus friends' spots you saved to
  // your map (spot_saves — the bookmark on a friend's spot card).
  const mySpots = useMemo(
    () => (spots || []).filter((s) => s.user_id === userId || s.savedByMe),
    [spots, userId]
  );

  // Search sections read the RAW past data (map filters don't trim search).
  const searchOwnBeen = useMemo(() => {
    const ids = new Set();
    for (const g of pastGroups || []) {
      if (
        g.nights.some((n) => n.entries.some((e) => e.user_id === userId)) ||
        g.marks.some((m) => m.user_id === userId)
      )
        ids.add(g.venue.id);
    }
    return ids;
  }, [pastGroups, userId]);
  const searchFriendInfo = useMemo(() => {
    const map = new Map();
    for (const g of pastGroups || []) {
      const names = [];
      const seen = new Set();
      let nights = 0;
      for (const n of g.nights) {
        const friendEntries = n.entries.filter((e) => e.user_id !== userId);
        if (friendEntries.length === 0) continue;
        nights++;
        for (const e of friendEntries) {
          if (!seen.has(e.user_id)) {
            seen.add(e.user_id);
            names.push(
              (e.profile?.display_name || "A friend").split(" ")[0]
            );
          }
        }
      }
      for (const m of g.marks) {
        if (m.user_id !== userId && !seen.has(m.user_id)) {
          seen.add(m.user_id);
          names.push((m.profile?.display_name || "A friend").split(" ")[0]);
        }
      }
      if (seen.size > 0)
        map.set(g.venue.id, { names: names.slice(0, 2), count: nights || seen.size });
    }
    return map;
  }, [pastGroups, userId]);

  // PERSON FILTER (profile "Places"): the whole trail of ONE friend — every
  // venue they've checked in, grouped per venue with visit counts. RLS
  // trims to friends-only for free.
  const [personPins, setPersonPins] = useState([]);
  useEffect(() => {
    if (!personFilter?.userId) {
      setPersonPins([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data: rows } = await supabase
        .from("activities")
        .select("venue_id, created_at, label")
        .eq("kind", "checkin")
        .eq("user_id", personFilter.userId)
        .order("created_at", { ascending: false })
        .limit(200);
      const byVenue = new Map();
      for (const r of rows || []) {
        if (!r.venue_id) continue;
        if (!byVenue.has(r.venue_id)) byVenue.set(r.venue_id, []);
        byVenue.get(r.venue_id).push(r);
      }
      const ids = Array.from(byVenue.keys());
      if (ids.length === 0) {
        if (!cancelled) setPersonPins([]);
        return;
      }
      const { data: vens } = await supabase
        .from("venues")
        .select("*")
        .in("id", ids);
      if (cancelled) return;
      setPersonPins(
        (vens || [])
          .filter(
            (v) =>
              Number.isFinite(Number(v.latitude)) &&
              Number.isFinite(Number(v.longitude))
          )
          .map((v) => ({ venue: v, visits: byVenue.get(v.id) }))
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [personFilter?.userId]);

  // LIST VIEW (Sep 7, Mark: "can we also get a list view... regardless of
  // the filter select"): the same content the map is showing — segment,
  // lens, filter chips all live — rendered as rows instead of pins. Toggle
  // sits under the zoom stack. Venues sort rating-first (his call).
  const [listView, setListView] = useState(false);
  const [listQuery, setListQuery] = useState(""); // filter-this-list field

  // Past-lens tap: one night and nothing else = straight into the card (no
  // list of one); anything richer opens the venue's nights sheet.
  const [nightsSheet, setNightsSheet] = useState(null); // a past group or null
  function openNight(night, venue) {
    const t =
      night.entries.find((e) => e.user_id === userId) || night.entries[0];
    const isSelf = t.user_id === userId;
    onOpenThread?.({
      activityId: t.id,
      ownerId: t.user_id,
      ownerName: isSelf ? "You" : t.profile?.display_name || "A friend",
      ownerProfile: isSelf ? null : t.profile || null,
      venueName: venue.name,
      label: t.label || null,
      venueObj: venue,
      timestamp: t.created_at,
    });
  }
  function handlePastTap(group) {
    // ALWAYS the list first (Mark, Oct 9: "this should happen on all
    // occasions, whether you have been there once or more") — one
    // consistent beat: pin → the venue's nights → a night's card.
    setNightsSheet(group);
  }

  // Group visible check-ins by venue — the per-viewer "Mark and John are at X"
  // clustering. One pin per venue, entries newest-first.
  const friendPins = useMemo(() => {
    if (mapFilter !== "friends" || !friendCheckins) return [];
    const groups = new Map();
    for (const c of friendCheckins) {
      const venue = c.venue; // resolved directly at fetch time
      if (
        !venue ||
        !Number.isFinite(Number(venue.latitude)) ||
        !Number.isFinite(Number(venue.longitude))
      )
        continue;
      // Map filters apply to Now too (Sep 9, Mark) — a coffee filter trims
      // presence pins the same as every other layer.
      if (!matchesMapFilters(venue)) continue;
      if (!groups.has(venue.id)) groups.set(venue.id, { venue, entries: [] });
      groups.get(venue.id).entries.push(c);
    }
    return Array.from(groups.values());
  }, [mapFilter, friendCheckins, matchesMapFilters]);

  const displayedPlottable = useMemo(() => {
    // LIST LENS (Oct 12, Mark): a list viewed on the map — only its
    // venues, and the normal filter chips keep working on top ("filter a
    // custom list"). Takes precedence over every other lens until the
    // chip's ✕ clears it.
    if (listFilter) {
      return (venues || []).filter(
        (v) =>
          listFilter.ids.has(v.id) &&
          Number.isFinite(Number(v.latitude)) &&
          Number.isFinite(Number(v.longitude)) &&
          matchesMapFilters(v)
      );
    }
    if (mapFilter === "friends") return friendPins.map((g) => g.venue);
    const todayKey = getTodayDayKey();
    // Browse map stays curated even though venue READS are open now: pins are
    // verified venues + your saved ones. A venue you created just to check in
    // (e.g. a concert hall) doesn't clutter your map unless you save it.
    let list =
      mapFilter === "my_list" && savedIds
        ? plottable.filter((v) => savedIds.has(v.id))
        : plottable.filter(
            (v) => v.verified === true || (savedIds && savedIds.has(v.id))
          );
    // My List's third lens (Sep 6, Mark): saved places you HAVEN'T been to —
    // the try-next map. Been = own nights + been-marks (searchOwnBeen).
    if (mapFilter === "my_list" && myListLens === "not_been")
      list = list.filter((v) => !searchOwnBeen.has(v.id));
    if (fAreas.length > 0)
      list = list.filter((v) =>
        venueMatchesAreas(v, fAreas, 0, mapAreaExtents)
      );
    if (fCuisines.length > 0)
      list = list.filter((v) => fCuisines.includes(v.cuisine_bucket));
    if (fOccasions.length > 0)
      list = list.filter((v) => venueMatchesOccasions(v, fOccasions, todayKey));
    if (fOpenNow) list = list.filter((v) => isVenueOpenNow(v));
    if (fMinRating > 0) list = list.filter((v) => Number(v.rating) >= fMinRating);
    if (fPrices.length > 0) list = list.filter((v) => venueMatchesPrice(v, fPrices));
    if (fAmenities.length > 0)
      list = list.filter((v) => venueMatchesAmenities(v, fAmenities));
    return list;
  }, [plottable, mapFilter, myListLens, searchOwnBeen, savedIds, fAreas, fCuisines, fOccasions, fOpenNow, fMinRating, fPrices, fAmenities, friendPins, listFilter, venues, matchesMapFilters]);

  const toggleOccasion = (v) =>
    setFOccasions((p) => (p.includes(v) ? p.filter((x) => x !== v) : [...p, v]));
  const toggleCuisine = (c) =>
    setFCuisines((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));
  const togglePrice = (p) =>
    setFPrices((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  const toggleAmenity = (k) =>
    setFAmenities((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
  const toggleArea = (a) =>
    setFAreas((p) =>
      p.some((x) => x.name === a.name)
        ? p.filter((x) => x.name !== a.name)
        : [...p, { name: a.name, lat: a.lat, lng: a.lng }]
    );
  const clearAll = () => {
    setFOccasions([]);
    setFCuisines([]);
    setFAreas([]);
    setFOpenNow(false);
    setFMinRating(0);
    setFPrices([]);
    setFAmenities([]);
  };

  const chips = [
    ...fAreas.map((a) => ({
      key: "area:" + a.name,
      label: a.name,
      onRemove: () => setFAreas((p) => p.filter((x) => x.name !== a.name)),
    })),
    ...fOccasions.map((v) => ({
      key: "occ:" + v,
      label: v,
      onRemove: () => setFOccasions((p) => p.filter((x) => x !== v)),
    })),
    ...fCuisines.map((c) => ({
      key: "cui:" + c,
      label: c,
      onRemove: () => setFCuisines((p) => p.filter((x) => x !== c)),
    })),
    ...(fOpenNow
      ? [{ key: "open", label: "Open now", onRemove: () => setFOpenNow(false) }]
      : []),
    ...(fOpenDay && fOpenTime
      ? [
          {
            key: "openat",
            label: `Open ${
              OPEN_AT_DAY_OPTIONS.find((d) => d.key === fOpenDay)?.label || ""
            } ${
              OPEN_AT_TIME_OPTIONS.find((t) => t.value === fOpenTime)?.label ||
              fOpenTime
            }`,
            onRemove: () => {
              setFOpenDay("");
              setFOpenTime("");
            },
          },
        ]
      : []),
    ...(fMinRating > 0
      ? [{ key: "rating", label: `${fMinRating}★+`, onRemove: () => setFMinRating(0) }]
      : []),
    ...[...fPrices]
      .sort((a, b) => a - b)
      .map((p) => ({
        key: "price:" + p,
        label: "$".repeat(p),
        onRemove: () => setFPrices((prev) => prev.filter((x) => x !== p)),
      })),
    ...fAmenities.map((k) => ({
      key: "amenity:" + k,
      label: (AMENITY_FILTERS.find((a) => a.key === k) || {}).label || k,
      onRemove: () => setFAmenities((prev) => prev.filter((x) => x !== k)),
    })),
  ];

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Venues inside the current viewport — the set the header count and the
  // card's venue-to-venue swipe cover. Markers still render the full filtered
  // set (clustering handles off-screen pins as you pan).
  const inViewPlottable = useMemo(() => {
    if (!mapBounds) return displayedPlottable;
    return displayedPlottable.filter((v) =>
      mapBounds.contains([Number(v.latitude), Number(v.longitude)])
    );
  }, [displayedPlottable, mapBounds]);

  // MAP → SESSION carry (Aug 21, Mark; viewport added Sep 6): snapshot the
  // active filters AND the venues currently on screen up to App, so starting
  // a session from the map's ⊕ inherits both — "the 6 coffee places I'm
  // looking at ARE the shortlist pool". Ref-stored there, read at FAB time.
  // NOTE: this effect must sit BELOW inViewPlottable's declaration (deps
  // array reads it during render — the TDZ trap class).
  useEffect(() => {
    onFiltersSnapshot?.({
      cuisines: fCuisines,
      areas: fAreas,
      occasions: fOccasions, // "What are you after?" — same chips as sessions
      openNow: fOpenNow,
      prices: fPrices,
      amenities: fAmenities,
      viewIds: inViewPlottable.map((v) => v.id),
      // Where the map is centred — a name-only spot's pin lands here.
      mapCenter: mapBounds
        ? { lat: mapBounds.getCenter().lat, lng: mapBounds.getCenter().lng }
        : null,
    });
  }, [fCuisines, fAreas, fOccasions, fOpenNow, fPrices, fAmenities, inViewPlottable, mapBounds]);

  // Position of the open card within the venues currently in view, so swiping
  // the card steps venue-to-venue through what's on screen. The order WRAPS:
  // opening the 3rd venue and swiping right goes 4, 5, …, end, then loops to
  // 1 and 2 — every in-view venue is reachable in one direction regardless of
  // which pin was tapped first. If the user pans away while a card is open the
  // card stays but next/prev simply disable (selectedIndex -1).
  const selectedIndex =
    selectedVenue != null
      ? inViewPlottable.findIndex((v) => v.id === selectedVenue.id)
      : -1;
  const canCycle = selectedIndex >= 0 && inViewPlottable.length > 1;
  const hasNext = canCycle;
  const hasPrev = canCycle;

  return (
    <div className="fixed inset-0 z-[1500] bg-white lg:left-[84px]">
      {/* FLOATING CHROME (Sep 6, Mark's new map UI): the white header bar is
          gone — the map runs full-bleed and the controls float on it. Row 1:
          segment pills + the search circle. Row 2: lens pills, active filter
          chips, count. The filter button moved to the bottom-right stack,
          above the plus. */}
      {!searchUi && (
        <div className="absolute top-0 left-0 right-0 z-[2000] px-4 pt-3 pointer-events-none">
          <div className="flex items-center gap-2.5 pointer-events-auto">
            <div className="flex flex-1 min-w-0 gap-0.5 rounded-full bg-white p-1 shadow-[0_2px_10px_rgba(30,27,23,0.14)] lg:flex-none">
              {[
                { key: "all", label: "All" },
                { key: "my_list", label: "Mine" },
                { key: "friends", label: "Friends" },
              ].map((seg) => (
                <button
                  key={seg.key}
                  type="button"
                  onClick={() => {
                    // A segment tap always answers "whose map" — it clears
                    // any one-friend trail view (Sep 6 field find: Emily's
                    // places survived a switch to My List).
                    if (personFilter) onClearPersonFilter?.();
                    setMapFilter(seg.key);
                  }}
                  className={`h-9 flex-1 rounded-full text-[13.5px] font-medium transition lg:flex-none lg:px-6 ${
                    mapFilter === seg.key
                      ? "bg-[#455d3b] text-white"
                      : "text-neutral-500"
                  }`}
                >
                  {seg.label}
                </button>
              ))}
            </div>
            {/* ONE SEARCH PER VIEW (Oct 16, Mark): in list view the
                magnifier retires — narrowing lives in the list's own box,
                global search behind its Search-everywhere row. */}
            {!listView && (
              <button
                type="button"
                onClick={() => setSearchUi(true)}
                aria-label="Search places"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-[#455d3b] shadow-[0_2px_10px_rgba(30,27,23,0.14)] active:scale-95 transition lg:hidden"
              >
                <Search size={17} strokeWidth={1.8} />
              </button>
            )}
          </div>
          <div className="mt-2.5 flex items-center gap-2 overflow-x-auto pb-1 pointer-events-auto">
            {(mapFilter === "friends" || mapFilter === "my_list") &&
              !personFilter && (
                <div className="flex shrink-0 gap-0.5 rounded-full bg-white p-[3px] shadow-[0_2px_10px_rgba(30,27,23,0.12)]">
                  {(mapFilter === "friends"
                    ? [
                        { key: "now", label: "Now" },
                        { key: "past", label: "Past" },
                        { key: "spots", label: "Spots" },
                      ]
                    : [
                        { key: "saved", label: "Saved" },
                        { key: "been", label: "Been" },
                        { key: "not_been", label: "Haven't been" },
                        { key: "spots", label: "Spots" },
                      ]
                  ).map((lens) => {
                    const active =
                      mapFilter === "friends"
                        ? friendLens === lens.key
                        : myListLens === lens.key;
                    return (
                      <button
                        key={lens.key}
                        type="button"
                        onClick={() =>
                          mapFilter === "friends"
                            ? setFriendLens(lens.key)
                            : setMyListLens(lens.key)
                        }
                        className={`h-[30px] rounded-full px-3.5 text-[13px] font-medium transition ${
                          active
                            ? "bg-[#edf2eb] text-[#455d3b]"
                            : "text-neutral-500"
                        }`}
                      >
                        {lens.label}
                      </button>
                    );
                  })}
                </div>
              )}
          </div>
          {/* Active filter chips on their OWN row (Sep 6, Mark: they were
              getting cut off sharing a line with the lens pills). */}
          {chips.length > 0 && (
            <div className="mt-2 flex items-center gap-2 overflow-x-auto pb-1 pointer-events-auto">
              {chips.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={c.onRemove}
                  className="shrink-0 inline-flex h-8 items-center gap-1.5 rounded-full border border-[#c7d4c0] bg-[#e7ede3] pl-3 pr-2 text-[13px] font-medium text-[#33402c] shadow-sm"
                >
                  {c.label}
                  <X size={11} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Place count — bottom centre above the tab bar (Sep 6, Mark: the
          row-2 tally got cut off once the number grew). */}
      {!searchUi && !personFilter && (
        <div className="absolute left-1/2 -translate-x-1/2 z-[2050] bottom-[104px] lg:bottom-[24px]">
          <span className="block rounded-full bg-white/95 px-3.5 py-1.5 text-[12.5px] font-medium text-neutral-700 shadow-[0_2px_10px_rgba(30,27,23,0.14)] whitespace-nowrap">
              {mapFilter === "friends" && friendLens === "spots"
                ? `${friendSpots.length} ${
                    friendSpots.length === 1 ? "spot" : "spots"
                  }`
                : mapFilter === "my_list" && myListLens === "spots"
                ? `${mySpots.length} ${
                    mySpots.length === 1 ? "spot" : "spots"
                  }`
                : mapFilter === "friends" && friendLens === "past"
                ? `${friendPastPins.length} ${
                    friendPastPins.length === 1 ? "place" : "places"
                  }`
                : mapFilter === "my_list" && myListLens === "been"
                ? `${myBeenPins.length} ${
                    myBeenPins.length === 1 ? "place" : "places"
                  }`
                : mapFilter === "friends"
                ? `${(friendCheckins || []).length} ${
                    (friendCheckins || []).length === 1 ? "friend" : "friends"
                  } out`
                : `${inViewPlottable.length} ${
                    inViewPlottable.length === 1 ? "place" : "places"
                  }`}
          </span>
        </div>
      )}
      <div className="absolute left-0 right-0 bottom-0 top-0">
        <MapContainer
          center={MELBOURNE_CENTER}
          zoom={MELBOURNE_ZOOM}
          zoomControl={false}
          style={{ height: "100%", width: "100%" }}
        >
          <MapResizer />
          <BoundsWatcher onBounds={setMapBounds} />
          <MapRef mapRef={mapRef} />
          {/* Positron with Mark's CARTO basemaps key (Aug 21 — CARTO began
              watermarking keyless tiles). The key is public + domain-
              restricted, so client code is its home. Keyless OSM fallback
              if the key ever lapses:
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" */}
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=cb1_2i1k_1_fe5697f3857f77adc7cfbe45"
          />
          {personFilter ? (
            // One friend's trail — their places, their pins.
            personPins.map((group) => (
              <Marker
                key={`p_${group.venue.id}`}
                position={[
                  Number(group.venue.latitude),
                  Number(group.venue.longitude),
                ]}
                icon={createPersonIcon(group, personFilter.profile)}
                eventHandlers={{
                  click: () => setSelectedVenue(group.venue),
                }}
              />
            ))
          ) : mapFilter === "my_list" && myListLens === "spots" ? (
            // YOUR spots.
            mySpots.map((s) => (
              <Marker
                key={`myspot_${s.id}`}
                position={[Number(s.lat), Number(s.lng)]}
                icon={createSpotIcon(s.category)}
                eventHandlers={{
                  click: () => onOpenSpot?.(s),
                }}
              />
            ))
          ) : mapFilter === "friends" && friendLens === "spots" ? (
            // The friend knowledge layer: FRIENDS' spots only, category
            // emoji pins, tap for the card with directions.
            friendSpots.map((s) => (
              <Marker
                key={`spot_${s.id}`}
                position={[Number(s.lat), Number(s.lng)]}
                icon={createSpotIcon(s.category)}
                eventHandlers={{
                  click: () => onOpenSpot?.(s),
                }}
              />
            ))
          ) : mapFilter === "friends" && friendLens === "past" ? (
            // The friends memory map: every venue where a FRIEND has a night
            // or mark (your own footprint pins under My List's Been lens).
            // Pin weight = what lives there: a friend's avatar, an olive
            // count chip, or a quiet been-mark dot.
            <MarkerClusterGroup
              chunkedLoading
              disableClusteringAtZoom={16}
              spiderfyOnMaxZoom={true}
              showCoverageOnHover={false}
              maxClusterRadius={50}
            >
              {friendPastPins.map((group) => {
                const friendOnly = group.friendNights.map((n) => ({
                  entries: n.entries.filter((e) => e.user_id !== userId),
                }));
                const friendPeople = new Set(
                  friendOnly.flatMap((n) => n.entries.map((e) => e.user_id))
                );
                const icon =
                  friendOnly.length === 0
                    ? createPastMarkIcon()
                    : friendOnly.length === 1 && friendPeople.size === 1
                    ? createPastSingleIcon(friendOnly[0].entries[0].profile)
                    : createPastClusterIcon({ nights: friendOnly });
                return (
                  <Marker
                    key={`past_${group.venue.id}`}
                    position={[
                      Number(group.venue.latitude),
                      Number(group.venue.longitude),
                    ]}
                    icon={icon}
                    eventHandlers={{
                      click: () => handlePastTap(group),
                    }}
                  />
                );
              })}
            </MarkerClusterGroup>
          ) : mapFilter === "my_list" && myListLens === "been" ? (
            // YOUR been map: every venue you've had a night at or ticked the
            // Been pill on, pinned as you. Tap = the full picture, you and
            // friends together.
            <MarkerClusterGroup
              chunkedLoading
              disableClusteringAtZoom={16}
              spiderfyOnMaxZoom={true}
              showCoverageOnHover={false}
              maxClusterRadius={50}
            >
              {myBeenPins.map((group) => {
                const ownOnly = group.ownNights.map((n) => ({
                  entries: n.entries.filter((e) => e.user_id === userId),
                }));
                const icon =
                  ownOnly.length > 1
                    ? createPastClusterIcon({ nights: ownOnly })
                    : createPastSingleIcon(group.ownProfile);
                return (
                  <Marker
                    key={`been_${group.venue.id}`}
                    position={[
                      Number(group.venue.latitude),
                      Number(group.venue.longitude),
                    ]}
                    icon={icon}
                    eventHandlers={{
                      click: () => handlePastTap(group),
                    }}
                  />
                );
              })}
            </MarkerClusterGroup>
          ) : mapFilter === "friends" ? (
            // Friend pins: one per venue, avatar stack + name label, no
            // clustering (there are few, and each pin IS the information).
            friendPins.map((group) => (
              <Marker
                key={`f_${group.venue.id}`}
                position={[
                  Number(group.venue.latitude),
                  Number(group.venue.longitude),
                ]}
                icon={createFriendsIcon(group)}
                eventHandlers={{
                  click: () => setSelectedVenue(group.venue),
                }}
              />
            ))
          ) : (
            <MarkerClusterGroup
              chunkedLoading
              disableClusteringAtZoom={17}
              spiderfyOnMaxZoom={true}
              showCoverageOnHover={false}
              maxClusterRadius={60}
            >
              {displayedPlottable.map((venue) => (
                <Marker
                  key={venue.id}
                  position={[Number(venue.latitude), Number(venue.longitude)]}
                  icon={createEmojiIcon(getVenueEmoji(venue))}
                  eventHandlers={{
                    click: () => setSelectedVenue(venue),
                  }}
                />
              ))}
            </MarkerClusterGroup>
          )}
        </MapContainer>
      </div>
      {/* THE LIST VIEW — same content as the pins, as rows. Header pills
          stay live above it, so lens/filter switching updates in place. */}
      {listView && !personFilter && !searchUi && (
        <div
          className="absolute inset-x-0 bottom-0 z-[1900] overflow-y-auto overscroll-contain bg-white"
          style={{ top: chips.length > 0 ? 150 : 110 }}
        >
          <div className="mx-auto w-full max-w-lg px-4 pb-32 pt-1">
            {/* Filter-this-list (Sep 9, Mark: "search should also work") —
                narrows the rows by name/suburb/cuisine, per surface. */}
            <div className="sticky top-0 z-10 bg-white pb-2 pt-2">
              <input
                value={listQuery}
                onChange={(e) => setListQuery(e.target.value)}
                placeholder="Search this list"
                className="h-10 w-full rounded-full border border-neutral-200 bg-neutral-50 px-4 text-base focus:border-[#455d3b] focus:outline-none placeholder:text-neutral-400"
              />
            </div>
            {(() => {
              const q = listQuery.trim().toLowerCase();
              const hit = (...fields) =>
                !q ||
                fields.some((f) =>
                  String(f || "").toLowerCase().includes(q)
                );
              const rowShell = (key, onClick, left, title, sub, right, historyVenue = null) => {
                // THE ACCORDION on the map's list view too (Oct 15, Mark) —
                // chevron on venues YOU'VE been to; expansion = VenueHistory
                // (address, Nights | Media, your visits only).
                const canExpand =
                  historyVenue && searchOwnBeen.has(historyVenue.id);
                const isOpen = canExpand && expandedListVenue === historyVenue.id;
                const rowBtn = (
                  <button
                    key={canExpand ? undefined : key}
                    type="button"
                    onClick={onClick}
                    className="flex w-full min-w-0 flex-1 items-center gap-3 py-3 text-left active:bg-neutral-50"
                  >
                    {left}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14.5px] font-medium text-neutral-900">
                        {title}
                      </span>
                      {sub && (
                        <span className="block truncate text-[12.5px] text-neutral-500">
                          {sub}
                        </span>
                      )}
                    </span>
                    {right}
                  </button>
                );
                if (!canExpand)
                  return (
                    <div key={key} className="border-b border-neutral-100">
                      {rowBtn}
                    </div>
                  );
                return (
                  <div key={key} className="border-b border-neutral-100">
                    <div className="flex items-center gap-1">
                      {rowBtn}
                      <button
                        type="button"
                        aria-label={isOpen ? "Collapse" : "Your history here"}
                        onClick={() =>
                          setExpandedListVenue(isOpen ? null : historyVenue.id)
                        }
                        className={`shrink-0 p-2 text-sm ${
                          isOpen ? "text-[#455d3b]" : "text-neutral-300"
                        }`}
                      >
                        {isOpen ? "▾" : "▸"}
                      </button>
                    </div>
                    {isOpen && (
                      <div className="pb-3">
                        <VenueHistory
                          userId={userId}
                          venue={historyVenue}
                          onOpenThread={onOpenThread}
                        />
                      </div>
                    )}
                  </div>
                );
              };
              // Google photo refs need the key-adding proxy (same rule as the
              // hero); R2/storage URLs serve raw. Initial sits BEHIND the img
              // so a dead URL degrades to the letter, never a broken glyph.
              const venueThumb = (v) => {
                const raw = v.primary_image || null;
                const src = raw
                  ? raw.includes("googleapis.com")
                    ? `/api/place-photo?url=${encodeURIComponent(raw)}`
                    : raw
                  : null;
                return (
                  <span className="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#edf2eb] text-sm font-semibold text-[#455d3b]">
                    {(v.name || "?").charAt(0).toUpperCase()}
                    {src && (
                      <img
                        src={src}
                        alt=""
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    )}
                  </span>
                );
              };
              const ratingChip = (v) =>
                Number(v.rating) > 0 ? (
                  <span className="flex shrink-0 items-center gap-1 text-[12.5px] font-medium text-neutral-600">
                    <Star size={12} className="text-amber-400" fill="#fbbf24" />
                    {v.rating}
                  </span>
                ) : null;
              const empty = (text) => (
                <p className="pt-8 text-center text-sm text-neutral-400">{text}</p>
              );

              if (mapFilter === "friends" && friendLens === "now") {
                const list = friendPins.filter((g) =>
                  hit(g.venue.name, g.venue.suburb, g.entries[0].profile?.display_name)
                );
                if (list.length === 0) return empty("No friends out right now");
                return list.map((g) =>
                  rowShell(
                    `now_${g.venue.id}`,
                    () => setSelectedVenue(g.venue),
                    venueThumb(g.venue),
                    g.venue.name,
                    `${(g.entries[0].profile?.display_name || "A friend").split(" ")[0]}${
                      g.entries.length > 1 ? ` + ${g.entries.length - 1}` : ""
                    } · ${timeAgoShort(g.entries[0].created_at)}`,
                    ratingChip(g.venue)
                  )
                );
              }
              if (mapFilter === "friends" && friendLens === "past") {
                const list = friendPastPins.filter((g) =>
                  hit(g.venue.name, g.venue.suburb, g.venue.cuisine_bucket)
                );
                if (list.length === 0) return empty("No friend nights yet");
                return list.map((g) =>
                  rowShell(
                    `fp_${g.venue.id}`,
                    () => handlePastTap(g),
                    venueThumb(g.venue),
                    g.venue.name,
                    [g.venue.cuisine_bucket, g.venue.suburb].filter(Boolean).join(" · "),
                    <span className="shrink-0 rounded-full bg-[#edf2eb] px-2.5 py-0.5 text-[11.5px] font-medium text-[#455d3b]">
                      {g.friendNights.length || g.friendMarks.length}{" "}
                      {g.friendNights.length === 1 ? "night" : g.friendNights.length > 1 ? "nights" : "been"}
                    </span>
                  )
                );
              }
              if (
                (mapFilter === "friends" && friendLens === "spots") ||
                (mapFilter === "my_list" && myListLens === "spots")
              ) {
                const list = (
                  mapFilter === "friends" ? friendSpots : mySpots
                ).filter((s) => hit(s.title, s.place_name, s.category));
                if (list.length === 0) return empty("No spots yet");
                return list.map((s) =>
                  rowShell(
                    `sp_${s.id}`,
                    () => onOpenSpot?.(s),
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#edf2eb] text-lg">
                      {spotCategory(s.category).emoji}
                    </span>,
                    s.title,
                    s.place_name || null,
                    null
                  )
                );
              }
              if (mapFilter === "my_list" && myListLens === "been") {
                const list = myBeenPins.filter((g) =>
                  hit(g.venue.name, g.venue.suburb, g.venue.cuisine_bucket)
                );
                if (list.length === 0) return empty("Nowhere marked been yet");
                return list.map((g) =>
                  rowShell(
                    `mb_${g.venue.id}`,
                    () => handlePastTap(g),
                    venueThumb(g.venue),
                    g.venue.name,
                    [g.venue.cuisine_bucket, g.venue.suburb].filter(Boolean).join(" · "),
                    ratingChip(g.venue),
                    g.venue
                  )
                );
              }
              const venues = displayedPlottable
                .filter((v) => hit(v.name, v.suburb, v.cuisine_bucket))
                .sort(
                  (a, b) => (Number(b.rating) || 0) - (Number(a.rating) || 0)
                );
              if (venues.length === 0) return empty("Nothing matches right now");
              return venues.map((v) =>
                rowShell(
                  `v_${v.id}`,
                  () => setSelectedVenue(v),
                  venueThumb(v),
                  v.name,
                  [v.cuisine_bucket || v.type, v.suburb].filter(Boolean).join(" · "),
                  ratingChip(v),
                  v
                )
              );
            })()}
            {/* THE ESCAPE HATCH (Oct 16, Mark's one-search spec): local
                narrowing above, global finding one tap away — carries the
                query into the inline search (Flanit + Google, fly-to, Add). */}
            <button
              type="button"
              onClick={() => {
                setQ(listQuery.trim());
                setSearchUi(true);
              }}
              className="mt-1 flex w-full items-center gap-3 border-t border-neutral-100 py-3 text-left"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#edf2eb] text-[#455d3b]">
                <Search size={15} strokeWidth={1.8} />
              </span>
              <span className="flex-1 text-sm font-medium text-[#455d3b]">
                {listQuery.trim()
                  ? `Search everywhere for "${listQuery.trim()}"`
                  : "Search everywhere"}
              </span>
              <span className="text-[#455d3b]">›</span>
            </button>
          </div>
          {/* Readability wash for the floating controls (Sep 9, Mark) —
              rows fade out behind the +, filter and map-toggle buttons. */}
          <div className="pointer-events-none sticky bottom-0 -mt-28 h-28 bg-gradient-to-t from-white via-white/85 to-transparent" />
        </div>
      )}
      {!personFilter && !searchUi && !listView && mapFilter === "my_list" && myListLens === "not_been" && pastGroups !== null && displayedPlottable.length === 0 && (
        <div className="absolute left-1/2 -translate-x-1/2 z-[2100] max-w-[85%]" style={{ top: 120 }}>
          <div className="rounded-2xl bg-white/95 border border-neutral-100 shadow-lg px-4 py-3 text-center">
            <p className="text-sm font-medium text-neutral-800">
              You've been to everything on your list
            </p>
            <p className="text-xs text-neutral-500 mt-0.5">
              Save some new spots to try next
            </p>
          </div>
        </div>
      )}
      {/* LIST LENS CHIP (Oct 12) — the list's sticker face on the map;
          ✕ returns to the normal lenses. */}
      {listFilter && !personFilter && (
        <div
          className="absolute left-1/2 -translate-x-1/2 z-[2100]"
          style={{ top: 120 }}
        >
          <button
            type="button"
            onClick={onClearListFilter}
            className="flex items-center gap-2 rounded-full bg-white/95 border border-neutral-100 shadow-lg px-4 py-2 text-xs font-medium text-neutral-800 active:scale-95 transition"
          >
            <span>{listFilter.emoji}</span>
            {listFilter.name} · {displayedPlottable.length}
            <span className="text-neutral-400">✕</span>
          </button>
        </div>
      )}
      {personFilter && (
        <div
          className="absolute left-1/2 -translate-x-1/2 z-[2100]"
          style={{ top: 120 }}
        >
          <button
            type="button"
            onClick={onClearPersonFilter}
            className="flex items-center gap-2 rounded-full bg-white/95 border border-neutral-100 shadow-lg px-4 py-2 text-xs font-medium text-neutral-800 active:scale-95 transition"
          >
            {(personFilter.profile?.display_name || "Their").split(" ")[0]}'s
            places · {personPins.length}
            <span className="text-neutral-400">✕</span>
          </button>
        </div>
      )}
      {!personFilter && !searchUi && !listView && mapFilter === "friends" && friendLens === "spots" && spots !== null && friendSpots.length === 0 && (
        <div className="absolute left-1/2 -translate-x-1/2 z-[2100] max-w-[85%]" style={{ top: 120 }}>
          <div className="rounded-2xl bg-white/95 border border-neutral-100 shadow-lg px-4 py-3 text-center">
            <p className="text-sm font-medium text-neutral-800">
              No friend spots yet
            </p>
            <p className="text-xs text-neutral-500 mt-0.5">
              Their toilets, study spots and parks will land here
            </p>
          </div>
        </div>
      )}
      {!personFilter && !searchUi && !listView && mapFilter === "my_list" && myListLens === "spots" && spots !== null && mySpots.length === 0 && (
        <div className="absolute left-1/2 -translate-x-1/2 z-[2100] max-w-[85%]" style={{ top: 120 }}>
          <div className="rounded-2xl bg-white/95 border border-neutral-100 shadow-lg px-4 py-3 text-center">
            <p className="text-sm font-medium text-neutral-800">
              No spots yet
            </p>
            <p className="text-xs text-neutral-500 mt-0.5">
              Know a good toilet, study spot or park? Add it from the plus
            </p>
          </div>
        </div>
      )}
      {!personFilter && !listView && mapFilter === "friends" && friendLens === "now" && friendCheckins !== null && friendPins.length === 0 && (
        <div className="absolute left-1/2 -translate-x-1/2 z-[2100] max-w-[85%]" style={{ top: 120 }}>
          <div className="rounded-2xl bg-white/95 border border-neutral-100 shadow-lg px-4 py-3 text-center">
            <p className="text-sm font-medium text-neutral-800">
              No friends out in the last 24 hours
            </p>
            <p className="text-xs text-neutral-500 mt-0.5">
              Check in somewhere and get things moving
            </p>
          </div>
        </div>
      )}
      {!personFilter && !listView && mapFilter === "friends" && friendLens === "past" && pastGroups !== null && friendPastPins.length === 0 && (
        <div className="absolute left-1/2 -translate-x-1/2 z-[2100] max-w-[85%]" style={{ top: 120 }}>
          <div className="rounded-2xl bg-white/95 border border-neutral-100 shadow-lg px-4 py-3 text-center">
            <p className="text-sm font-medium text-neutral-800">
              No friend nights on the map yet
            </p>
            <p className="text-xs text-neutral-500 mt-0.5">
              Their check-ins and been spots will live here
            </p>
          </div>
        </div>
      )}
      {!personFilter && mapFilter === "my_list" && myListLens === "been" && pastGroups !== null && myBeenPins.length === 0 && (
        <div className="absolute left-1/2 -translate-x-1/2 z-[2100] max-w-[85%]" style={{ top: 120 }}>
          <div className="rounded-2xl bg-white/95 border border-neutral-100 shadow-lg px-4 py-3 text-center">
            <p className="text-sm font-medium text-neutral-800">
              Nowhere marked been yet
            </p>
            <p className="text-xs text-neutral-500 mt-0.5">
              Check in, or tick Been on a venue you know
            </p>
          </div>
        </div>
      )}
      {/* Desktop search: a real centred field over the map (the mock's call:
          "not a mystery circle"). Opens the same search UI. */}
      {!searchUi && !personFilter && (
        <button
          type="button"
          onClick={() => setSearchUi(true)}
          className="hidden lg:flex absolute left-1/2 top-[18px] z-[2000] h-11 w-[320px] -translate-x-1/2 items-center gap-2.5 rounded-full border border-[#ede7df] bg-white px-4 text-left shadow-[0_2px_10px_rgba(30,27,23,0.14)] hover:border-[#ddd7ce] transition"
        >
          <Search size={15} className="shrink-0 text-neutral-400" />
          <span className="text-[14.5px] text-neutral-400">Search places</span>
        </button>
      )}
      {/* Zoom stack (left) + the relocated filter button (right, above the
          plus FAB — same sheet, same filters, new home). */}
      {!searchUi && !personFilter && (
        <>
          {!listView && (
          <div className="absolute left-4 bottom-[140px] z-[2050] w-11 overflow-hidden rounded-xl bg-white shadow-[0_2px_10px_rgba(30,27,23,0.14)] lg:bottom-[18px]">
            <button
              type="button"
              aria-label="Zoom in"
              onClick={() => mapRef.current?.zoomIn()}
              className="flex h-11 w-11 items-center justify-center border-b border-neutral-100 text-[#455d3b] active:bg-neutral-50"
            >
              <Plus size={17} strokeWidth={1.9} />
            </button>
            <button
              type="button"
              aria-label="Zoom out"
              onClick={() => mapRef.current?.zoomOut()}
              className="flex h-11 w-11 items-center justify-center text-[#455d3b] active:bg-neutral-50"
            >
              <Minus size={17} strokeWidth={1.9} />
            </button>
          </div>
          )}
          <button
            type="button"
            aria-label={listView ? "Show the map" : "Show as a list"}
            onClick={() => {
              setListView((v) => !v);
              setListQuery("");
            }}
            className="absolute left-4 bottom-[84px] z-[2050] flex h-11 w-11 items-center justify-center rounded-xl bg-white text-[#455d3b] shadow-[0_2px_10px_rgba(30,27,23,0.14)] active:scale-95 transition lg:bottom-[74px]"
          >
            {listView ? (
              <MapIcon size={17} strokeWidth={1.8} />
            ) : (
              <List size={17} strokeWidth={1.8} />
            )}
          </button>
          <button
            type="button"
            aria-label="Filters"
            onClick={() => setShowFilters(true)}
            className="absolute right-4 bottom-[140px] z-[2050] flex h-12 w-12 items-center justify-center rounded-full bg-white text-[#455d3b] shadow-[0_2px_10px_rgba(30,27,23,0.16)] active:scale-95 transition lg:bottom-[86px]"
          >
            <SlidersHorizontal size={17} strokeWidth={1.8} />
            {activeCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-white bg-[#c0492f] px-1 text-[11px] font-semibold text-white">
                {activeCount}
              </span>
            )}
          </button>
        </>
      )}

      {/* INLINE SEARCH (Sep 6 — no Recent list; typing searches places live).
          Field bar over the map, results panel beneath, sections leading
          with yours, then friends', then the map, then Google + Add. */}
      {searchUi && (
        <div className="absolute inset-0 z-[2500]">
          <div className="absolute inset-x-0 top-0 px-4 pt-3 lg:mx-auto lg:max-w-[560px]">
            <div className="flex h-11 items-center gap-1.5 rounded-full bg-white pl-1.5 pr-1.5 shadow-[0_2px_12px_rgba(30,27,23,0.16)]">
              <button
                type="button"
                aria-label="Back to the map"
                onClick={closeSearch}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#455d3b] active:bg-neutral-100"
              >
                <ChevronLeft size={16} strokeWidth={2} />
              </button>
              {/* text-base: sub-16px inputs make iOS Safari auto-zoom. */}
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={
                  mapFilter === "friends"
                    ? "Search friends or places"
                    : "Search places"
                }
                className="h-11 min-w-0 flex-1 bg-transparent text-base text-neutral-900 placeholder:text-neutral-400 focus:outline-none"
              />
              {q && (
                <button
                  type="button"
                  aria-label="Clear"
                  onClick={() => setQ("")}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-500 active:bg-neutral-200"
                >
                  <X size={12} strokeWidth={2} />
                </button>
              )}
            </div>
          </div>
          <div className="absolute inset-x-0 bottom-0 top-[68px] overflow-y-auto overscroll-contain rounded-t-[22px] bg-white px-5 pb-8 shadow-[0_-4px_24px_rgba(30,27,23,0.14)] lg:inset-x-auto lg:left-1/2 lg:w-[560px] lg:-translate-x-1/2 lg:bottom-auto lg:max-h-[70vh] lg:rounded-[22px] lg:shadow-[0_8px_32px_rgba(30,27,23,0.2)]">
            {(() => {
              const friendSection =
                friendMatches.length > 0 ? (
                  <>
                    <p className="pt-4 pb-0.5 text-[11.5px] font-medium uppercase tracking-[0.1em] text-[#a79e90]">
                      Friends
                    </p>
                    {friendMatches.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          closeSearch();
                          onPersonFilter?.({ userId: p.id, profile: p });
                        }}
                        className="flex w-full items-center gap-3 py-[11px] text-left active:bg-neutral-50"
                      >
                        {p.avatar_url ? (
                          <img
                            src={p.avatar_url}
                            alt=""
                            className="h-[34px] w-[34px] shrink-0 rounded-full object-cover bg-neutral-100"
                          />
                        ) : (
                          <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-[#455d3b] text-[13px] font-semibold text-white">
                            {(p.display_name || "?").trim().charAt(0).toUpperCase()}
                          </span>
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[14.5px] text-neutral-900">
                            {p.display_name || p.username || "A friend"}
                          </span>
                          <span className="block truncate text-[12.5px] text-neutral-400">
                            See their places
                          </span>
                        </span>
                      </button>
                    ))}
                  </>
                ) : null;
              if (q.trim().length < 2)
                return (
                  <>
                    {friendSection}
                    {!friendSection && (
                      <p className="pt-6 text-sm text-neutral-400">
                        {mapFilter === "friends"
                          ? "Search friends or places"
                          : "Search places, like Market Lane"}
                      </p>
                    )}
                  </>
                );
              const dbRows = searchRes.venues;
              const sMine = dbRows.filter(
                (v) =>
                  (savedIds && savedIds.has(v.id)) || searchOwnBeen.has(v.id)
              );
              const mineIds = new Set(sMine.map((v) => v.id));
              const sFriends = dbRows.filter(
                (v) => !mineIds.has(v.id) && searchFriendInfo.has(v.id)
              );
              const friendIds = new Set(sFriends.map((v) => v.id));
              const sMap = dbRows.filter(
                (v) => !mineIds.has(v.id) && !friendIds.has(v.id)
              );
              const nothing =
                sMine.length + sFriends.length + sMap.length === 0 &&
                searchRes.google.length === 0;
              const label = (text) => (
                <p className="pt-4 pb-0.5 text-[11.5px] font-medium uppercase tracking-[0.1em] text-[#a79e90]">
                  {text}
                </p>
              );
              const row = (v, badge, sub) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => {
                    closeSearch();
                    flyToVenue(v);
                  }}
                  className="flex w-full items-center gap-3 py-[11px] text-left active:bg-neutral-50"
                >
                  <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-neutral-100">
                    <MapPin size={15} className="text-neutral-400" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14.5px] text-neutral-900">
                      {v.name}
                    </span>
                    <span className="block truncate text-[12.5px] text-neutral-400">
                      {sub ||
                        [v.cuisine_bucket, v.suburb]
                          .filter(Boolean)
                          .join(" · ") ||
                        "On the map"}
                    </span>
                  </span>
                  {badge}
                </button>
              );
              const chip = (text) => (
                <span className="flex h-6 shrink-0 items-center rounded-full bg-[#e7ede3] px-2.5 text-[11.5px] font-medium text-[#33402c]">
                  {text}
                </span>
              );
              return (
                <>
                  {friendSection}
                  {searching && (
                    <p className="pt-4 text-xs text-neutral-400">Searching…</p>
                  )}
                  {sMine.length > 0 && label("In My List · Been")}
                  {sMine.map((v) =>
                    row(
                      v,
                      chip(searchOwnBeen.has(v.id) ? "Been" : "Saved")
                    )
                  )}
                  {sFriends.length > 0 && label("Friends have been")}
                  {sFriends.map((v) => {
                    const info = searchFriendInfo.get(v.id);
                    return row(
                      v,
                      chip(info.count),
                      [
                        v.cuisine_bucket,
                        v.suburb,
                        info.names.join(", "),
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    );
                  })}
                  {sMap.length > 0 && label("On the curated map")}
                  {sMap.map((v) => row(v, null))}
                  {searchRes.google.length > 0 && label("Not on the map yet")}
                  {searchRes.google.map((r) => (
                    <button
                      key={r.place_id}
                      type="button"
                      disabled={addingId === r.place_id}
                      onClick={async () => {
                        if (addingId) return;
                        setAddingId(r.place_id);
                        try {
                          const venue = await addGooglePlace(r.place_id);
                          onVenueAdded?.(venue, { saved: false });
                          closeSearch();
                          flyToVenue(venue);
                        } catch (e) {
                          console.error("Search add failed:", e);
                          showToast?.("Couldn't add that place");
                        } finally {
                          setAddingId(null);
                        }
                      }}
                      className="flex w-full items-center gap-3 py-[11px] text-left active:bg-neutral-50 disabled:opacity-60"
                    >
                      <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border border-dashed border-neutral-300 bg-neutral-50">
                        <Search size={14} className="text-neutral-400" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[14.5px] text-neutral-900">
                          {r.name}
                        </span>
                        <span className="block truncate text-[12.5px] text-neutral-400">
                          {r.address || "From search"}
                        </span>
                      </span>
                      <span className="flex h-6 shrink-0 items-center gap-1 rounded-full border border-dashed border-[#c7d4c0] px-2.5 text-[11.5px] font-medium text-[#4e5c45]">
                        {addingId === r.place_id ? (
                          "Adding…"
                        ) : (
                          <>
                            <Plus size={9} strokeWidth={2.2} />
                            Add
                          </>
                        )}
                      </span>
                    </button>
                  ))}
                  {!searching && nothing && !friendSection && (
                    <p className="pt-5 text-sm text-neutral-400">
                      Nothing found for "{q.trim()}"
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {nightsSheet && (
        <VenueNightsSheet
          group={nightsSheet}
          userId={userId}
          onClose={() => setNightsSheet(null)}
          onOpenNight={(night) => {
            const venue = nightsSheet.venue;
            setNightsSheet(null);
            openNight(night, venue);
          }}
          // Venue name → the venue card (Mark, Oct 9: "the venue should
          // also be clickable in the list to display the card").
          onOpenVenue={(venue) => {
            setNightsSheet(null);
            setSelectedVenue(venue);
          }}
        />
      )}
      {selectedVenue && (
        <MapVenueSheet
          venue={selectedVenue}
          onClose={() => setSelectedVenue(null)}
          // hideMap REMOVED (Oct 13, Mark: "you lose where you are") — the
          // big map is behind the card but COVERED by it; the card's own
          // mini map → full screen → ✕ is the only loop that returns here.
          savedIds={savedIds}
          onSave={onSave}
          onUnsave={onUnsave}
          onHide={onHide}
          onCheckIn={onCheckIn}
          onOpenThread={onOpenThread}
          onOpenProfile={onOpenProfile}
          userId={userId}
          hasNext={hasNext}
          hasPrev={hasPrev}
          onNext={() =>
            hasNext &&
            setSelectedVenue(
              inViewPlottable[(selectedIndex + 1) % inViewPlottable.length]
            )
          }
          onPrev={() =>
            hasPrev &&
            setSelectedVenue(
              inViewPlottable[
                (selectedIndex - 1 + inViewPlottable.length) %
                  inViewPlottable.length
              ]
            )
          }
        />
      )}
      {showSearch && (
        <AddVenueSheet
          onClose={() => setShowSearch(false)}
          showToast={showToast}
          onOpenVenue={flyToVenue}
          onAdded={(venue, opts) => {
            onVenueAdded?.(venue, opts);
            // Check-in-only add: glide the map there but keep the card shut —
            // the confetti sheet is the moment.
            flyToVenue(venue, { openCard: opts?.saved !== false });
          }}
          onCheckInAfterAdd={(venue) => onCheckIn?.(venue)}
        />
      )}
      {showFilters &&
        createPortal(
          <div className="fixed inset-0 z-[3200]">
            <button
              type="button"
              aria-label="Close filters"
              onClick={() => setShowFilters(false)}
              className="absolute inset-0 bg-black/30"
            />
            <div className="absolute left-0 right-0 bottom-0 max-h-[85%] flex flex-col bg-white rounded-t-3xl shadow-2xl">
              <div className="px-5 pt-3 pb-2 border-b border-neutral-100">
                <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-neutral-200" />
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-semibold">Filters</h2>
                  {activeCount > 0 && (
                    <button
                      type="button"
                      onClick={clearAll}
                      className="text-xs font-medium text-red-600"
                    >
                      Clear all
                    </button>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-2">
                {(myLists || []).length > 0 && (
                  <MapFilterSection
                    title="Your lists"
                    summary={listFilter ? listFilter.name : "Any"}
                    accent={!!listFilter}
                  >
                    <div className="flex flex-wrap gap-1.5">
                      {(myLists || []).map((l) => (
                        <button
                          key={l.id}
                          type="button"
                          onClick={() => pickListFilter(l)}
                          className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                            listFilter?.id === l.id
                              ? "border-[#455d3b] bg-[#edf2eb] text-[#455d3b]"
                              : "border-neutral-200 bg-white text-neutral-600"
                          }`}
                        >
                          {l.emoji || "📍"} {l.name}
                        </button>
                      ))}
                    </div>
                  </MapFilterSection>
                )}

                {areas.length > 0 && (
                  <MapFilterSection
                    title="Area"
                    summary={fAreas.length ? `${fAreas.length} selected` : "Any"}
                    accent={fAreas.length > 0}
                  >
                    <MapAreaFilter areas={areas} selected={fAreas} onToggle={toggleArea} />
                  </MapFilterSection>
                )}

                {cuisineOptions.length > 0 && (
                  <MapFilterSection
                    title="Cuisine"
                    summary={fCuisines.length ? `${fCuisines.length} selected` : "Any"}
                    accent={fCuisines.length > 0}
                  >
                    <SearchableChips
                      options={cuisineOptions}
                      selected={fCuisines}
                      onToggle={toggleCuisine}
                      placeholder="Search cuisine"
                    />
                  </MapFilterSection>
                )}

                <div className="space-y-5 pt-4">
                  <MapFilterGroup title="What are you after?">
                    {OCCASION_OPTIONS.map((v) => (
                      <MapFilterChip
                        key={v}
                        on={fOccasions.includes(v)}
                        label={v}
                        onClick={() => toggleOccasion(v)}
                      />
                    ))}
                  </MapFilterGroup>

                  <MapFilterGroup title="Minimum rating">
                    {[0, 4, 4.5].map((r) => (
                      <MapFilterChip
                        key={r}
                        on={fMinRating === r}
                        label={r === 0 ? "Any" : `${r}★+`}
                        onClick={() => setFMinRating(r)}
                      />
                    ))}
                  </MapFilterGroup>

                  <MapFilterGroup title="Price">
                    {[1, 2, 3, 4].map((p) => (
                      <MapFilterChip
                        key={p}
                        on={fPrices.includes(p)}
                        label={"$".repeat(p)}
                        onClick={() => togglePrice(p)}
                      />
                    ))}
                  </MapFilterGroup>

                  <MapFilterGroup title="Must-haves">
                    {AMENITY_FILTERS.map((a) => (
                      <MapFilterChip
                        key={a.key}
                        on={fAmenities.includes(a.key)}
                        label={a.label}
                        onClick={() => toggleAmenity(a.key)}
                      />
                    ))}
                  </MapFilterGroup>

                  <div className="flex items-center justify-between pt-1">
                    <span className="text-sm font-medium text-neutral-800">
                      Open now
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={fOpenNow}
                      onClick={() => setFOpenNow((v) => !v)}
                      className={`relative w-11 h-6 rounded-full transition ${
                        fOpenNow ? "bg-[#455d3b]" : "bg-neutral-300"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                          fOpenNow ? "right-0.5" : "left-0.5"
                        }`}
                      />
                    </button>
                  </div>

                  {/* OPEN AT (Oct 30, Mark: "opening hours as a filter...
                      maybe in the advanced filters") — a specific day + time
                      for planning ahead; Open now stays the live shortcut.
                      Both selects set = active; Any on either = off. */}
                  <div className="flex items-center justify-between pt-3">
                    <span className="text-sm font-medium text-neutral-800">
                      Open at
                    </span>
                    <span className="flex items-center gap-1.5">
                      <select
                        value={fOpenDay}
                        onChange={(e) => setFOpenDay(e.target.value)}
                        className="appearance-none rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-base text-neutral-800 focus:outline-none"
                      >
                        <option value="">Any day</option>
                        {OPEN_AT_DAY_OPTIONS.map((d) => (
                          <option key={d.key} value={d.key}>
                            {d.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={fOpenTime}
                        onChange={(e) => setFOpenTime(e.target.value)}
                        className="appearance-none rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-base text-neutral-800 focus:outline-none"
                      >
                        <option value="">Any time</option>
                        {OPEN_AT_TIME_OPTIONS.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </span>
                  </div>
                </div>
              </div>

              <div className="px-5 py-3 border-t border-neutral-100">
                <button
                  type="button"
                  onClick={() => setShowFilters(false)}
                  className="w-full rounded-2xl bg-[#455d3b] py-3 font-medium text-white"
                >
                  Show {displayedPlottable.length}{" "}
                  {displayedPlottable.length === 1 ? "place" : "places"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
